import { useLayoutEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth, useResetQueryCacheOnUserChange } from "@bw/shared/hooks";

import { resetSettingsAuthority } from "@/modules/settings/settingsAuthorityStore";
import { resetPlatformAdminAccessAuthority } from "@/shared/platformAdminAccessAuthority";

/**
 * Keeps Query data and QueryClient-scoped privileged UI state owned by the
 * same authenticated actor. Descendants stay gated while the shared cache
 * boundary clears the previous actor's Query data and these stores reset.
 */
export function PlatformAuthoritySessionBoundary({
  children,
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const { loading, user } = useAuth();
  const queryCacheReady = useResetQueryCacheOnUserChange();
  const currentActorId = loading ? undefined : (user?.uid ?? null);
  const previousActorId = useRef<string | null | undefined>(undefined);

  useLayoutEffect(() => {
    if (currentActorId === undefined) return;
    const previousActor = previousActorId.current;
    if (previousActor !== undefined && previousActor !== currentActorId) {
      resetPlatformAdminAccessAuthority(queryClient);
      resetSettingsAuthority(queryClient);
    }
    previousActorId.current = currentActorId;
  }, [currentActorId, queryClient]);

  return queryCacheReady ? <>{children}</> : null;
}
